import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "The vibecoding addiction";
export const excerpt =
  "Vibe coding runs on the same reward schedule as a slot machine: unpredictable payouts, near-misses that feel like progress, and the pull of one more prompt. The research is starting to catch up.";
export const heroAlt =
  "Moebius-style landscape: a glowing winding trail of lights crossing a vast grassy plain toward a sunset horizon, tiny figures watching from a flowered dune under pastel clouds";
export const readingTime = "6 min read";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "M. Fawzy, A. Tahir, K. Blincoe, “Vibe Coding in Practice: Motivations, Challenges, and a Future Outlook: A Grey Literature Review” (2025). Used for the finding that 64% of practitioners reported “instant success and flow,” described as magical, a dopamine hit, and addictive momentum, and for the resulting false confidence in code quality.",
    href: "https://arxiv.org/abs/2510.00328",
    label: "arxiv.org/abs/2510.00328",
  },
  {
    text: "R. Thomas, “Breaking the Spell of Vibe Coding,” fast.ai (January 2026). Used for the dark flow / junk flow framing, the loss-disguised-as-a-win mechanic, and the contrast with Csikszentmihalyi’s skill-building flow.",
    href: "https://www.fast.ai/posts/2026-01-28-dark-flow/",
    label: "fast.ai/posts/2026-01-28-dark-flow",
  },
  {
    text: "L. Clark, A. J. Lawrence, F. Astley-Jones, N. Gray, “Gambling Near-Misses Enhance Motivation to Gamble and Recruit Win-Related Brain Circuitry,” Neuron (2009). Used for the finding that near-misses recruit win-related brain circuitry and increase the urge to keep playing despite no monetary reward.",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2658737/",
    label: "ncbi.nlm.nih.gov/…/Clark-2009-near-misses",
  },
  {
    text: "H. W. Chase, L. Clark, “Gambling Severity Predicts Midbrain Response to Near-Miss Outcomes,” Journal of Neuroscience (2010). Used for the finding that the strength of the midbrain dopaminergic response to near-misses tracked with gambling severity.",
    href: "https://www.jneurosci.org/content/30/18/6180",
    label: "jneurosci.org/content/30/18/6180",
  },
  {
    text: "METR, “Measuring the Impact of Early-2025 AI on Experienced Open-Source Developer Productivity” (2025). Used for the randomized trial in which developers expected a ~24% speedup, perceived a ~20% speedup, and were measured 19% slower with AI tools.",
    href: "https://arxiv.org/abs/2507.09089",
    label: "arxiv.org/abs/2507.09089",
  },
  {
    text: "A. Filev, “One More Prompt: How Vibe Coding’s Casino Mechanics Cost Me a Billion Dollars,” Unite.AI (October 2025). Used for the variable-reward and near-miss framing, the “one more prompt” loop, and the point that the pull is strongest for people new to building software.",
    href: "https://www.unite.ai/one-more-prompt-how-vibe-codings-casino-mechanics-cost-me-a-billion-dollars/",
    label: "unite.ai/…/one-more-prompt-casino-mechanics",
  },
  {
    text: "“Professional Software Developers Don’t Vibe, They Control: AI Agent Use for Coding in 2025” (2025). Used for the finding that experienced developers tend to steer and reject AI output rather than surrender to the loop.",
    href: "https://arxiv.org/abs/2512.14012",
    label: "arxiv.org/abs/2512.14012",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        I want to describe a specific evening, because I think a lot of people have had the same one. You open
        the editor to fix one small thing. You write a prompt and the model gets close. Not quite, but close.
        So you write another, and another. At some point you look up and three hours are gone, the feature still
        does not work, and your strongest feeling is the urge to send one more prompt.
      </Lead>

      <P>
        That last part is the tell. Not the lost time, people lose time to good work too. The tell is the pull,
        the wanting-to-keep-going that has quietly detached from whether it is going well.
      </P>
      <P>
        There is now a small but growing pile of writing, some of it academic, arguing that vibe coding is not
        simply productive or unproductive. It is structurally addictive, in close to the technical sense. It
        runs on the same reward schedule that makes a slot machine hard to walk away from.
      </P>

      <Section title="One more prompt">
        <P>
          Andrew Filev, who runs an AI coding company, wrote about catching himself in it. He was on holiday,
          family waiting at the beach, telling himself &ldquo;just one more try&rdquo; for hours while the
          prompt almost worked.<Fn n={6} /> He is not a casual user. He builds these tools for a living. He
          still ends up describing the experience in the language of a casino: unpredictable rewards,
          near-misses, the one-more-prompt loop he could not quite close.
        </P>
        <P>
          What makes his account useful is that he is honest about who it hits hardest. For an experienced
          engineer the AI is an optimisation, a faster version of something they could already do. For someone
          who could not build software at all and suddenly can, the jump is enormous, and so is the
          dopamine.<Fn n={6} /> The bigger the gap between &ldquo;I could not&rdquo; and &ldquo;I just
          did,&rdquo; the stronger the pull.
        </P>
      </Section>

      <Section title="It runs on the same schedule as a slot machine">
        <P>
          Behavioural psychology has a name for the most stubborn reward pattern we know: the variable-ratio
          schedule. You get a reward after an unpredictable number of tries. Not every time, not on a fixed
          count, just often enough that you can never predict the next payout. Pigeons, rats, and people will
          all keep working a lever far longer under that schedule than under a predictable one. It is the engine
          inside a slot machine.
        </P>
        <P>
          Vibe coding fits the pattern almost too well. Sometimes the first prompt nails it. Sometimes the
          eighth does, and sometimes you burn an hour for nothing. The payout is real, code that works, but its
          timing is random, and random timing is exactly what keeps the lever moving.
        </P>
        <P>
          Old-fashioned coding paid out slowly. You might go hours between the small wins of something compiling
          or a test turning green. AI coding compresses that to seconds. The grey-literature review I keep
          coming back to found that the single most common reported experience, 64 percent of practitioners in
          their sample, was &ldquo;instant success and flow,&rdquo; often described in those exact words:
          magical, a dopamine hit, addictive momentum.<Fn n={1} /> Fast payouts on a random schedule, which is
          the whole recipe a casino runs on.
        </P>
      </Section>

      <Section title="The hook">
        <P>
          The slot-machine comparison gets sharper at the near-miss. A near-miss is the spin where two cherries
          land on the line and the third stops just above it. Objectively it is a loss, identical to any other
          loss. It does not feel like one.
        </P>
        <P>
          Luke Clark and colleagues put gamblers in an fMRI scanner and showed that near-misses recruit much of
          the same win-related brain circuitry as actual wins, and that people reported a stronger urge to keep
          playing right after a near-miss, even though they had won nothing.<Fn n={3} /> A later study found
          that the size of that midbrain response to near-misses tracked with how severe a person&rsquo;s
          gambling was.<Fn n={4} /> The almost-win is not a failure state for the brain. It is a second kind of
          reward.
        </P>
        <P>
          Now think about what a coding agent produces when it is wrong. Not garbage. Usually something that
          almost works. The code compiles and fails one test. The page renders but the button does nothing. It
          is the near-miss, generated on demand, dozens of times an hour. Each one says you are so close, try
          again, and unlike a slot machine it is often telling the truth, which makes it much harder to ignore.
        </P>
      </Section>

      <Section title="Flow, or a cheap copy of it">
        <P>
          Defenders of vibe coding reach for the word flow, and it is a fair instinct. The state feels like
          flow: absorbed, time gone, friction low. Rachel Thomas at fast.ai makes the case that it is closer to
          what researchers call dark flow or junk flow, the kind you get from a slot machine or an infinite
          feed.<Fn n={2} /> Real flow, in Csikszentmihalyi&rsquo;s original sense, comes from a task matched to
          your skill and builds the skill as you go. Junk flow is the absorption without the growth. It feels
          the same from the inside, and you come out the other side no better, sometimes worse.
        </P>
        <P>
          The grey-lit review found the same dark edge in its data. The instant-success experience produced
          what the authors call false confidence in the quality of the code.<Fn n={1} /> The feeling of having
          done good work and the fact of having done it had come apart. That is the part that should worry you,
          more than the lost hours.
        </P>
      </Section>

      <Section title="It feels like progress. The numbers disagree.">
        <P>
          Slot researchers have a term for the cruelest trick on the floor: a loss disguised as a win. The
          machine pays back less than you bet but still runs the full song and lights, so a net loss registers
          as a win.<Fn n={2} /> Vibe coding does something similar with productivity. It feels fast. It is loud
          about its own progress. The measured result is less flattering.
        </P>
        <P>
          METR ran a randomised trial in early 2025 with experienced open-source developers working on real
          tasks in their own repositories. Beforehand, the developers expected AI to speed them up by about 24
          percent. Afterwards they believed it had sped them up by about 20 percent. Measured, they were 19
          percent slower with the AI tools than without.<Fn n={5} /> They felt faster and were slower, a gap of
          nearly 40 points between what they felt and what happened. In slot terms, that is a loss disguised as
          a win, this time measured in a controlled trial with professional engineers.
        </P>
      </Section>

      <Section title="Is it actually addiction?">
        <P>
          I want to be careful here, because it is easy to over-claim. There is no diagnosis called
          vibe-coding disorder. Nobody has run the long clinical studies you would need to call this an
          addiction the way gambling disorder is an addiction. What we have is a mechanism that matches,
          variable-ratio rewards and potent near-misses, plus a lot of people independently reaching for the
          same gambling vocabulary to describe their own behaviour. That is suggestive, not settled.
        </P>
        <P>
          It also does not hit everyone the same way. A recent study with the blunt title &ldquo;Professional
          Software Developers Don&rsquo;t Vibe, They Control&rdquo; found that experienced engineers tend not to
          surrender to the loop at all. They steer the agent, reject most of what it offers, and keep a model of
          the system in their own head.<Fn n={7} /> The pull is strongest exactly where the safety rails are
          thinnest: people new to building software, moving fast, with no tests and no clear sense of when they
          are done.
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="References">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
